import { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { Button, Image, message } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { SwiperSlide } from "swiper/react"
import SwiperSelf from "@/components/SwiperSelf"
import useCartActions from "@/utils/addTocartAction"
import myAccountApi from "@/service/modules/myAccountApi"
import viewcartApi from "@/service/modules/viewcartApi"
import { formatCurrency, getCompressedImageUrl } from "@/utils/utils"
import styles from "@/styles/view-cart.module.scss"

interface RecommendationSectionProps {
  type: 'wishilist' | 'related'
  list?: any[]
  successAddCart: (_res: any) => void
  isLogin?: boolean
}

const RecommendationSection = ({ type, list, successAddCart, isLogin }: RecommendationSectionProps) => {
  const swiper: any = useRef(null)
  const [recommendList, setRecommendList] = useState<any>([])

  const relatedListKey = useMemo(() => {
    if (type !== 'related') return ''
    return (list || []).map((item: any) => `${item.productID}:${item.categoryID}`).join('|')
  }, [type, list])

  useEffect(() => {
    if (!isLogin && type == 'wishilist') return
    if (type == 'related' && !(list && list.length)) return

    let cancelled = false

    const getRecommend = async () => {
      try {
        const params = type == 'related' ? { items: (list || []).map((item: any) => ({ productID: item.productID, categoryID: item.categoryID })) } : {}
        const apiFunction = type == 'related' ? viewcartApi.related : myAccountApi.getWishList
        const res = await apiFunction(params)

        if (cancelled) return

        setRecommendList(type == 'related' ? res.data.pageList.items : res.data.wishProducts.filter((v: any) => v.inStock > 0))
      } catch (error: any) {
        // Keep the cart page usable even when recommendations are unavailable.
      }
    }

    getRecommend()

    return () => {
      cancelled = true
    }
  }, [isLogin, type, relatedListKey])

  return (
    recommendList.length
      ?
      <div className={styles['recomment-box']}>
        <div className={styles['recomment-title']}>
          <div>{type == 'wishilist' ? 'Your Wish List:' : 'Customer Who Bought These Products Also Bought:'}</div>
          <div className={styles['page-control']}>
            <LeftOutlined className={styles['control-icon']} onClick={() => { swiper.current.slidePrev() }} />
            <RightOutlined className={styles['control-icon']} onClick={() => { swiper.current.slideNext() }} />
          </div>
        </div>
        <div className={styles['recomment-list']}>
          <SwiperSelf
            slidesPerView={5}
            autoplay={false}
            hideArrows={true}
            spaceBetween={10}
            ref={swiper}
          >
            {
              recommendList.map((item: any) => (
                <SwiperSlide key={item.productID}>
                  <ViewCartGoodsCard {...item} successAddCart={successAddCart} />
                </SwiperSlide>
              ))
            }
          </SwiperSelf>
        </div>
      </div>
      :
      <></>
  )
}

const ViewCartGoodsCard = ({ filename, url, name, price, normalPrice, productID, successAddCart }: any) => {
  const { addToCart } = useCartActions()
  const [loadingBtn, setLoadingBtn] = useState(false)

  const addToCartCallBack = (response: any) => {
    if (response != "error") {
      successAddCart(response)
      message.success("Add to cart successfully!")
    }
    setLoadingBtn(false)
  }

  const addCart = (event: any) => {
    setLoadingBtn(true)
    addToCart(event, { productID, qty: 1 }, addToCartCallBack)
  }

  const priceArr = `${price}`.split('.')

  return (
    <div className={styles['viewcar-component']}>
      <Link href={url} legacyBehavior>
        <a rel="noopener noreferrer" style={{ width: '100%', display: 'block', aspectRatio: '1 / 1', overflow: 'hidden' }}>
          <Image
            alt={name || ''}
            src={getCompressedImageUrl(filename, 227)}
            preview={false}
            width="100%"
            height="100%"
            style={{ objectFit: "contain" }}
          />
        </a>
      </Link>
      <Link href={url} legacyBehavior>
        <a rel="noopener noreferrer" className={styles['goods-name']}>
          {name}
        </a>
      </Link>
      <div className={styles['price-line']}>
        <div className={styles['price']}>${priceArr[0]}.<span>{priceArr[1]}</span></div>
        {
          normalPrice
          &&
          <div className={styles['cross-price']}>{formatCurrency(normalPrice)}</div>
        }
      </div>
      <Button loading={loadingBtn} type="primary" className={styles['addcart-btn']} onClick={addCart} >Add to Cart</Button>
    </div>
  )
}

export default RecommendationSection
